import {customElement, property} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {FORM_ELEMENT_INIT_EVENT, type FormInitEvent, initStepEvent} from "./formEvents";
import type {FormInputElement} from "./components/formInputElement";
import {ValidationError} from "./validations/validations";
import {ScrollService} from "../../common/scroll";
import {resolve} from "../../container";
import {UnLitElement} from "../../common/elements";
import type {FormFieldConfig, FormSectionConfig, FormStepConfig} from "./formConfig";
import {renderField} from "./formFieldRenderer";
import {headline, headlineLevel, headlineSize} from "../../common/snippets/headline";

export type StepData = {
    id: string;
    label: string;
    nextButtonLabel: string;
    prevButtonLabel: string;
    validate: ((silent?: boolean) => boolean);
};

@customElement("eop-form-step")
export class EopFormStep extends UnLitElement {

    @property({type: Object})
    public stepConfig: FormStepConfig;

    private validations: ((silent: boolean) => void)[];

    public constructor(
        private scrollService: ScrollService = resolve(ScrollService)
    ) {
        super();
        this.validations = [];
        this.addEventListener(FORM_ELEMENT_INIT_EVENT, event => {
            const eventData = (event as FormInitEvent).detail;
            this.validations.push(eventData.validate);
        });
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dispatchEvent(initStepEvent({
            id: this.stepConfig.id,
            label: this.stepConfig.label,
            nextButtonLabel: this.stepConfig.nextLabel,
            prevButtonLabel: this.stepConfig.prevLabel ?? "",
            validate: silent => this.validate(silent)
        }));
    }

    public disconnectedCallback(): void {
        this.validations = [];
        super.disconnectedCallback();
    }

    public render(): TemplateResult {
        return html`
            ${this.stepConfig.headline && this.stepConfig.headlineLevel ? headline(headlineLevel(this.stepConfig.headlineLevel), this.stepConfig.headline, [headlineSize(this.stepConfig.headlineLevel)]) : null}
            ${this.renderSections()}
        `;
    }

    private renderSections(): TemplateResult[] {
        return this.stepConfig.sections.map(section => html`${this.renderSection(section)}`);
    }

    private renderSection(section: FormSectionConfig): TemplateResult {
        return html`
            <eop-form-section
                    section-id=${section.id}
                    condition=${section.condition ?? ""}
            >
                ${section.headline && section.headlineLevel ? headline(headlineLevel(section.headlineLevel), section.headline, [headlineSize(section.headlineLevel)]) : null}
                <div class="form-elements">
                    ${this.renderFormElements(section.elements)}
                </div>
            </eop-form-section>
        `;
    }

    private renderFormElements(elements: FormFieldConfig[]): TemplateResult[] {
        return elements.map(field => renderField(field));
    }

    private validate(silent: boolean = false): boolean {
        const invalidElements: FormInputElement<any>[] = [];

        for (const validation of this.validations) {
            try {
                validation(silent);
            } catch (e) {
                if (!(e instanceof ValidationError)) {
                    throw e;
                }
                invalidElements.push(e.invalidElement);
            }
        }

        if (invalidElements.isEmpty()) {
            return true;
        }

        if (!silent) {
            this.scrollToHighestElement(invalidElements);
        }
        return false;
    }

    private scrollToHighestElement(elements: FormInputElement<any>[]): void {
        const element: FormInputElement<any> = elements.clone().sort((prev, curr) => prev.offsetTop - curr.offsetTop).getFirst()!;
        void this.scrollService.scrollToElement(element);
        if (element.focusInput !== undefined) {
            element.focusInput();
        }
    }
}
